import { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utils & misc
import cn from '../../../utils/cn';
import { parseFontAwesomeClass } from '../../../utils/fontawesome/config';

// Assets
// eslint-disable-next-line import/no-unresolved
import Loader from '../../../assets/loaders/circle-loader.svg?react';
// Style
import style from './IconButton.module.css';
// Components
import Picto, { availablePictos } from '../Picto/Picto';

/**
 * Render the icon based on the icon type
 * @param {keyof typeof import('../Picto/Picto').availablePictos | string} icon - Icon name from available pictos or FontAwesome icon definition
 * @returns {JSX.Element} The icon component
 */
const renderIcon = (icon) => {
  const iconDefinition = parseFontAwesomeClass(icon);
  if (iconDefinition) {
    return <FontAwesomeIcon icon={/** @type {any} */ (iconDefinition)} />;
  }
  if (icon && Object.prototype.hasOwnProperty.call(availablePictos, icon)) {
    const pictoIcon = /** @type {keyof typeof import('../Picto/Picto').availablePictos} */ (icon);
    return <Picto icon={pictoIcon} className={style.icon} />;
  }
  return null;
};

/**
 * @typedef {object} IconButtonProps
 * @property {keyof typeof import('../Picto/Picto').availablePictos | string} [icon] - Icon name from available pictos or FontAwesome icon definition
 * @property {(event: React.MouseEvent<HTMLButtonElement>) => void} [onClick] - Click handler function
 * @property {'small'|'medium'|'large'} [size] - Button size
 * @property {'primary'|'secondary'|'primary100'|'neutral00'|'neutral200'|'alert'|'success'|'no-background'|'no-background-white'|'transparent'} [color] - Button color variant
 * @property {'' | 'primary' | 'secondary' | 'primary100' | 'neutral00' | 'neutral' | 'yellow-500'} [iconColor] - Icon color variant
 * @property {boolean} [isSubmit] - Whether button should submit form
 * @property {boolean} [isLoading] - Whether button is in loading state
 * @property {boolean} [disabled] - Whether button is disabled
 * @property {string} [className] - Additional CSS classes
 * @property {string} [form] - Form ID to associate with (for submit buttons)
 * @property {string} [title] - Tooltip text for accessibility
 * @property {boolean} [readOnly] - Whether button is read only
 */

/**
 * IconButton component - A button that displays only an icon
 * @param {IconButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>} props - Component props
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref for the button element
 * @returns {JSX.Element} IconButton component
 */
function IconButtonComponent({
  onClick,
  icon,
  size = 'large',
  color = 'primary',
  iconColor = '',
  isSubmit = false,
  isLoading = false,
  disabled = false,
  className,
  form,
  title,
  readOnly = false,
  ...props
}, ref) {
  return (
    <button
      ref={ref}
      className={cn([
        style['icon-button'],
        style[color],
        style[`icon-${iconColor}`],
        style[size],
        isLoading && style['is-loading'],
        readOnly && style['read-only'],
        className,
      ])}
      type={isSubmit ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled || isLoading}
      form={form}
      title={title}
      aria-label={title || `Icon button with ${icon} icon`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {isLoading ? (
        <Loader className={style.loader} />
      ) : (
        renderIcon(icon)
      )}
    </button>
  );
}

const IconButton = forwardRef(IconButtonComponent);

export default IconButton;
